import { useCallback, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUser } from '../../../lib/auth';
import { ISSUE_STATUS_OPTIONS } from '../../../lib/issueTypeOptions';

export const BOARD_STATUSES = ISSUE_STATUS_OPTIONS.map(option => option.value);
export const BOARD_PREFERENCES_KEY = 'overlayqa.issue-board.v1';
const CHANGE_EVENT = 'overlayqa:issue-board-preferences';
type Preferences = { visible: string[]; order: string[]; view: 'table' | 'board' };

function read(raw: string | null): Preferences {
  // The board is the view a project opens on; only a saved pick of the table
  // (setView) or a ?view=table link puts the list first.
  const defaults: Preferences = { visible: BOARD_STATUSES, order: [], view: 'board' };
  try {
    const saved = JSON.parse(raw || 'null');
    if (!saved) return defaults;
    const visible = Array.isArray(saved.visible) ? BOARD_STATUSES.filter(id => saved.visible.includes(id)) : [];
    return {
      visible: visible.length ? visible : BOARD_STATUSES,
      order: Array.isArray(saved.order) ? [...new Set<string>(saved.order.filter((id: unknown) => typeof id === 'string'))] : [],
      view: saved.view === 'table' ? 'table' : 'board',
    };
  } catch { return defaults; }
}

/** Only display preferences live here. Issue status always comes from the API. */
export function useIssueBoardPreferences(projectId: string) {
  const { user } = useUser();
  const key = `${BOARD_PREFERENCES_KEY}.${user?.id ?? 'signed-out'}.${projectId}`;
  const [params, setParams] = useSearchParams();
  const subscribe = useCallback((notify: () => void) => {
    const changed = (event: Event) => {
      if (event instanceof StorageEvent ? event.key === key || event.key === null : (event as CustomEvent).detail === key) notify();
    };
    window.addEventListener('storage', changed);
    window.addEventListener(CHANGE_EVENT, changed);
    return () => { window.removeEventListener('storage', changed); window.removeEventListener(CHANGE_EVENT, changed); };
  }, [key]);
  const snapshot = useCallback(() => { try { return localStorage.getItem(key); } catch { return null; } }, [key]);
  const raw = useSyncExternalStore(subscribe, snapshot, () => null);
  const preferences = read(raw);
  const urlView = params.get('view');
  const view = urlView === 'table' || urlView === 'board' ? urlView : preferences.view;

  function update(patch: Partial<Preferences>, failure: string) {
    try {
      localStorage.setItem(key, JSON.stringify({ ...read(snapshot()), ...patch }));
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: key }));
      return true;
    } catch { toast.error(failure); return false; }
  }
  function setVisible(next: string[]) {
    const visible = BOARD_STATUSES.filter(id => next.includes(id));
    if (!visible.length) { toast.info('Keep at least one column visible.'); return false; }
    return update({ visible }, 'Could not save column visibility. Try again.');
  }
  function setView(next: 'table' | 'board') {
    update({ view: next }, 'This view could not be remembered.');
    setParams(previous => { const nextParams = new URLSearchParams(previous); nextParams.set('view', next); return nextParams; }, { replace: true });
  }
  function place(id: string, beforeId: string | undefined, allIds: string[]) {
    const saved = read(snapshot()).order;
    const order = [...new Set([...saved.filter(item => allIds.includes(item)), ...allIds])].filter(item => item !== id);
    const index = beforeId ? order.indexOf(beforeId) : -1;
    order.splice(index < 0 ? order.length : index, 0, id);
    return update({ order }, 'Could not remember the card order. Your issue status is saved.');
  }
  return { ...preferences, view, setView, setVisible, place };
}
export type IssueBoardPreferences = ReturnType<typeof useIssueBoardPreferences>;
